import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { cx } from "shared/utils";
import { useRef } from "react";
import { Show } from "shared/components/helpers/Show";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components/IconButton";
import { Trash02 } from "untitledui-js-base";
import { toast } from "react-toastify";

const DEFAULT_MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const VALID_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/jpg",
  "image/svg+xml"
];

interface LogoDropZoneProps {
  disabled?: boolean;
  className?: string;
  logoUrl?: string;
  onFileSelect?: (file: File | null) => void;
  isProcessing?: boolean;
  processingLabel?: string;
  maxFileSize?: {
    size: number;
    message: string;
  };
}

export const LogoDropZone = ({
  logoUrl,
  disabled = false,
  className,
  onFileSelect,
  isProcessing = false,
  processingLabel = "Uploading...",
  maxFileSize
}: LogoDropZoneProps) => {
  const dictionary = useUnitsPropertiesDictionary();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > (maxFileSize?.size || DEFAULT_MAX_FILE_SIZE)) {
      toast.error(maxFileSize?.message || "File size should be less than 5MB");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    onFileSelect?.(file);
  };

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFileSelect?.(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="relative">
      <label
        tabIndex={0}
        className={cx(
          "animate-appear relative h-[118px] w-full flex items-center justify-center gap-2 rounded-lg border border-alpha-100 overflow-hidden cursor-pointer",
          !logoUrl && "border-dashed bg-background-900 hover:bg-background-800",
          disabled && "opacity-50 cursor-not-allowed",
          isProcessing && "opacity-70",
          className
        )}
      >
        <Show if={!logoUrl}>
          <div className="flex flex-col items-center justify-center gap-1 pb-6 pt-5">
            <p className="text-sm font-medium text-center text-content-400">
              Upload {dictionary.property} logo
            </p>
            <p className="text-sm text-center text-content-200">(Optional)</p>
          </div>
        </Show>

        {logoUrl ? (
          <img
            src={logoUrl}
            alt="Uploaded logo"
            className="max-h-10 object-contain"
          />
        ) : null}

        {isProcessing && (
          <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
            <div className="flex flex-col items-center gap-1">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span className="text-white text-xs font-medium">
                {processingLabel}
              </span>
            </div>
          </div>
        )}

        <input
          ref={fileInputRef}
          accept={VALID_FILE_TYPES.join(",")}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          onChange={handleFileChange}
          required={false}
          type="file"
          disabled={disabled || isProcessing}
        />
      </label>

      <Show if={logoUrl && !isProcessing}>
        <div className="absolute right-3 bottom-2" onClick={handleRemoveFile}>
          <IconButton
            size={IconButtonSize.SM}
            variant={IconButtonVariant.PRIMARY}
            Icon={Trash02}
            disabled={disabled || isProcessing}
            className="backdrop-blur-md !bg-alpha-600 hover:!bg-alpha-500 transition-colors"
          />
        </div>
      </Show>
    </div>
  );
};
